import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import type { VisitOfferListing } from '../../shared/staff-visit-offers.ts';
import { visitOfferCopy } from '../pages/staff-visit-offers.tsx';

/** A passive preview of the server-filtered list; decisions stay on the offer screen. */
export default function StaffVisitOffersFeed({ refreshKey = 0 }: { refreshKey?: number }) {
  const s = useSession();
  const scope = JSON.stringify([
    s.user?.companyId,
    s.user?.id,
    s.currentLocation?.id,
    s.user?.role,
    [...(s.user?.permissions ?? [])].sort(),
    [...(s.company?.planFeatures ?? [])].sort(),
    s.company?.accessLocked,
    s.impersonatedBy,
    refreshKey,
  ]);
  const allowed =
    !!s.user &&
    !!s.currentLocation &&
    !s.company?.accessLocked &&
    !s.impersonatedBy &&
    s.company?.planFeatures?.includes('staff_portal') === true &&
    s.user.permissions.includes('staff_portal:read');
  return allowed ? <ScopedFeed key={scope} /> : null;
}

function ScopedFeed() {
  const { i18n } = useTranslation(),
    spanish = i18n.language.startsWith('es'),
    copy = visitOfferCopy[spanish ? 'es' : 'en'];
  const [list, setList] = useState<VisitOfferListing | null>(null),
    [state, setState] = useState<'hidden' | 'loading' | 'ready' | 'error'>('hidden');
  const alive = useRef(true),
    generation = useRef(0);
  useEffect(() => {
    alive.current = true;
    void load();
    return () => {
      alive.current = false;
      generation.current++;
    };
  }, []);
  async function load() {
    const gen = ++generation.current;
    setList(null);
    // Hide the preview until this deployment's independent capability succeeds.
    setState('hidden');
    try {
      await api.get('/staff/visit-offers/capability');
      if (!alive.current || gen !== generation.current) return;
      setState('loading');
      const result = await api.get<VisitOfferListing>('/staff/visit-offers');
      if (!alive.current || gen !== generation.current) return;
      setList(result);
      setState('ready');
    } catch (error) {
      if (!alive.current || gen !== generation.current) return;
      const status = (error as { status?: number }).status;
      setList(null);
      setState(status === 401 || status === 402 || status === 403 || status === 404 ? 'hidden' : 'error');
    }
  }
  if (state === 'hidden') return null;
  const visible = list?.items.slice(0, 5) ?? [];
  function when(value: string, timezone: string) {
    return new Intl.DateTimeFormat(spanish ? 'es' : 'en', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: timezone,
    }).format(new Date(value));
  }
  return (
    <section className="min-w-0 space-y-3 rounded border p-4" aria-label={copy.title}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-bold">{copy.title}</h3>
        <button className="btn btn-secondary min-h-11" disabled={state === 'loading'} onClick={() => void load()}>
          {state === 'error'
            ? spanish
              ? 'Reintentar solicitudes'
              : 'Retry requests'
            : spanish
              ? 'Actualizar solicitudes'
              : 'Refresh requests'}
        </button>
      </div>
      {state === 'loading' && <p role="status">{copy.loading}</p>}
      {state === 'error' && <p role="alert">{copy.readFailed}</p>}
      {state === 'ready' && !visible.length && <p>{copy.empty}</p>}
      {visible.map((row) => (
        <article className="min-w-0 space-y-2 rounded border p-3" key={row.id}>
          <p className="break-words font-semibold">{row.clientName}</p>
          <p className="break-words text-sm">
            {when(row.startsAt, row.timezone)} / {when(row.endsAt, row.timezone)} ({row.timezone})
          </p>
          <p>
            {copy[row.status]}
            {row.own ? ` · ${copy.own}` : ''}
          </p>
          {row.urgent && <p>{copy.urgent}</p>}
          {row.stale && <p>{copy.stale}</p>}
          {row.note && <p className="line-clamp-3 whitespace-pre-wrap break-words">{row.note}</p>}
          <Link className="btn btn-secondary min-h-11 whitespace-normal" to="/staff/visit-offers">
            {list?.canManage
              ? spanish
                ? 'Revisar solicitud'
                : 'Review request'
              : spanish
                ? 'Ver solicitud'
                : 'View request'}
          </Link>
        </article>
      ))}
      {state === 'ready' && (
        <Link className="btn btn-secondary min-h-11 whitespace-normal" to="/staff/visit-offers">
          {spanish ? 'Ver todas las solicitudes' : 'View all requests'}
        </Link>
      )}
    </section>
  );
}
